import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Button
} from '@mui/material';
import {
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  Approval as ApprovalsIcon,
  Gavel as ApprovalActionIcon
} from '@mui/icons-material';
import { useMsal, useAccount } from '@azure/msal-react';

import { useAppSelector } from '../../../../app/store';
import { ApprovalView } from '../../../../services/RefundService/types/refundTypes';
import SectionPaper from '../../../common/components/styled-ui-elements/SectionPaper';
import { ApprovalDialog } from '../dialogs/ApprovalDialog';

// Constants
const APPROVAL_STATUS_CONFIG = {
  'Pending': { 
    color: 'warning' as const, 
    icon: <PendingIcon sx={{ fontSize: 16 }} />,
    bgColor: '#fff3cd',
    textColor: '#856404'
  },
  'Approved': { 
    color: 'success' as const, 
    icon: <ApprovedIcon sx={{ fontSize: 16 }} />,
    bgColor: '#d1edff',
    textColor: '#0c5460'
  },
  'Rejected': { 
    color: 'error' as const, 
    icon: <RejectedIcon sx={{ fontSize: 16 }} />,
    bgColor: '#f8d7da',
    textColor: '#721c24'
  }
} as const;

const SECTION_STYLES = {
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#f1f5f9'
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#374151',
      fontSize: '0.75rem',
      padding: '8px 6px',
      whiteSpace: 'nowrap'
    },
    '& .MuiTableCell-body': {
      fontSize: '0.75rem',
      padding: '6px 6px'
    },
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: '#f8fafc'
      }
    }
  }
} as const;

// Types
type ApprovalStatus = keyof typeof APPROVAL_STATUS_CONFIG;

// Helper functions
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return 'No limit';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const getApprovalThresholdText = (minAmount: number | null | undefined, maxAmount: number | null | undefined): string => {
  if (minAmount === null || minAmount === undefined) {
    if (maxAmount === null || maxAmount === undefined) return 'Any amount';
    return `Up to ${formatCurrency(maxAmount)}`;
  }
  if (maxAmount === null || maxAmount === undefined) return `${formatCurrency(minAmount)} and above`;
  return `${formatCurrency(minAmount)} - ${formatCurrency(maxAmount)}`;
};

const getApprovalStatus = (approval: ApprovalView): ApprovalStatus => {
  if (approval.isRejected) return 'Rejected';
  if (approval.isApproved) return 'Approved';
  return 'Pending';
};

// Status chip component
const ApprovalStatusChip: React.FC<{ status: ApprovalStatus }> = ({ status }) => {
  const config = APPROVAL_STATUS_CONFIG[status];
  
  return (
    <Chip
      icon={config.icon}
      label={status}
      size="small"
      sx={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 500,
        fontSize: '0.75rem',
        '& .MuiChip-icon': {
          color: config.textColor
        }
      }}
    />
  );
};

// Table row component with approval actions
const ApprovalRow: React.FC<{ 
  approval: ApprovalView; 
  index: number;
  onApprovalAction: (approval: ApprovalView, readOnly?: boolean) => void;
  isInApprovalStatus: boolean;
}> = ({ approval, index, onApprovalAction, isInApprovalStatus }) => {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const status = getApprovalStatus(approval);
  const approvedDate = approval.approvedDate || approval.rejectedDate;
  
  // Check if current user is authorized to approve/reject this level
  const userDisplayName = account?.name || '';
  const requiredApprover = approval.requiredApprover || '';
  const backupApprover = approval.backupApprover || '';
  
  // User can act if they are the assigned approver for this level
  const canApprove = (userDisplayName === requiredApprover || userDisplayName === backupApprover) && 
                     userDisplayName.length > 0;
  
  return (
    <TableRow hover>
      <TableCell>{approval.level}</TableCell>
      <TableCell>
        <Stack direction="column" spacing={0.25}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500, 
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '160px'
            }}
            title={approval.requiredApprover || 'Not assigned'}
          >
            {approval.requiredApprover || 'Not assigned'}
          </Typography>
          {approval.backupApprover && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#64748b', 
                fontSize: '0.7rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '160px'
              }}
              title={`Backup: ${approval.backupApprover}`}
            >
              Backup: {approval.backupApprover}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>{getApprovalThresholdText(approval.minAmount, approval.maxAmount)}</TableCell>
      <TableCell>
        <ApprovalStatusChip status={status} />
      </TableCell>
      <TableCell>
        {approvedDate ? new Date(approvedDate).toLocaleDateString() : '-'}
      </TableCell>
      <TableCell align="center">
        {isInApprovalStatus && canApprove ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<ApprovalActionIcon sx={{ fontSize: 12 }} />}
            onClick={() => onApprovalAction(approval)}
            sx={{
              minWidth: 70,
              fontSize: '0.65rem',
              padding: '3px 6px',
              backgroundColor: status === 'Pending' ? '#3b82f6' : '#f59e0b',
              '&:hover': {
                backgroundColor: status === 'Pending' ? '#2563eb' : '#d97706'
              }
            }}
          >
            {status === 'Pending' ? 'Act' : 'Change'}
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onApprovalAction(approval, true)}
            sx={{
              minWidth: 70,
              fontSize: '0.65rem',
              padding: '3px 6px',
              borderColor: '#94a3b8',
              color: '#64748b',
              '&:hover': {
                borderColor: '#64748b',
                backgroundColor: '#f8fafc'
              }
            }}
          >
            View
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

// Empty state component
const EmptyApprovals: React.FC = () => (
  <Box sx={{ textAlign: 'center', py: 4 }}>
    <Typography variant="body1" sx={{ color: '#64748b', mb: 1 }}>
      No approval levels configured
    </Typography>
    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
      Approval levels will be automatically added when the refund is created based on center configuration.
    </Typography>
  </Box>
);

// Main component
export const ApprovalsSection: React.FC = () => {
  const { currentRefund } = useAppSelector(state => state.refund);
  const [selectedApproval, setSelectedApproval] = useState<ApprovalView | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  
  const approvals = currentRefund?.approvals || [];
  const hasApprovals = approvals.length > 0;
  const isInApprovalStatus = currentRefund?.status === 'APPROVE';

  const handleApprovalAction = (approval: ApprovalView, readOnly: boolean = false) => {
    setSelectedApproval(approval);
    setIsReadOnly(readOnly);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedApproval(null);
    setIsReadOnly(false);
  };

  return (
    <>
      {/* Header Section */}
      <SectionPaper sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <ApprovalsIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  mb: 0.5
                }}
              >
                Approval Levels
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: '#64748b' }}
              >
                {approvals.length === 0 
                  ? 'No approval levels configured for this refund' 
                  : isInApprovalStatus
                    ? `${approvals.length} approval level${approvals.length !== 1 ? 's' : ''} - Actions available`
                    : `${approvals.length} approval level${approvals.length !== 1 ? 's' : ''} - View only`
                }
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </SectionPaper>

      {/* Approvals Table Section */}
      {!hasApprovals ? (
        <SectionPaper>
          <EmptyApprovals />
        </SectionPaper>
      ) : (
        <SectionPaper 
          sx={{ 
            border: '1px solid #e2e8f0',
            borderRadius: 1.5,
            overflow: 'hidden'
          }}
        >
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table 
              sx={{
                ...SECTION_STYLES.table,
                tableLayout: 'fixed',
                width: '720px'
              }} 
              size="small" 
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '60px' }}>Level</TableCell>
                  <TableCell sx={{ width: '180px' }}>Approver</TableCell>
                  <TableCell sx={{ width: '140px' }}>Threshold</TableCell>
                  <TableCell sx={{ width: '120px' }}>Status</TableCell>
                  <TableCell sx={{ width: '100px' }}>Date</TableCell>
                  <TableCell align="center" sx={{ width: '80px' }}>
                    {isInApprovalStatus ? 'Actions' : 'View'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvals.map((approval, index) => (
                  <ApprovalRow 
                    key={`${approval.level}-${index}`} 
                    approval={approval} 
                    index={index}
                    onApprovalAction={handleApprovalAction}
                    isInApprovalStatus={isInApprovalStatus}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionPaper>
      )}

      {/* Approval Dialog */}
      {selectedApproval && currentRefund && (
        <ApprovalDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          approval={selectedApproval}
          refundId={currentRefund.id}
          company={currentRefund.company}
          center={currentRefund.center}
          refundAmount={currentRefund.refundDetail.amount}
          readOnly={isReadOnly}
        />
      )}
    </>
  );
};
